import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  MiniMap,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  useNodesState,
  useEdgesState
} from 'reactflow';
import 'reactflow/dist/style.css';

const OptimizationApproaches = () => {
  const [selectedMethod, setSelectedMethod] = useState('nsga2');

  // Load saved positions from localStorage
  const loadSavedPositions = () => {
    const saved = localStorage.getItem('optimization-approaches-positions');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved positions:', e);
      }
    }
    return null;
  };

  const savedPositions = loadSavedPositions();

  const initialNodes = [
    // Objective Function (Starting Point)
    {
      id: 'objective-function',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'center' }}>
            <strong style={{ fontSize: '20px', color: '#FFFFFF' }}>🎯 Smart Objective Function</strong>
            <div style={{ fontSize: '16px', marginTop: '15px', color: '#FFE4E6', lineHeight: '1.8' }}>
              • Normalizes all metrics to comparable scales<br/>
              • Learns from your preferences over time<br/>
              • Balances competing objectives intelligently
            </div>
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              background: 'rgba(0, 0, 0, 0.4)', 
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <strong style={{ fontSize: '16px' }}>Input Vector:</strong>
              <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '14px', 
                marginTop: '8px',
                color: '#FCD34D'
              }}>
                [Speed: 5, Memory: 4, Readability: 2, Tests: 5]
              </div>
            </div>
          </div>
        )
      },
      position: savedPositions?.['objective-function'] || { x: 350, y: 50 },
      style: {
        background: 'linear-gradient(135deg, #6D28D9 0%, #4C1D95 100%)',
        border: '2px solid #8B5CF6',
        borderRadius: '12px',
        width: 300,
        color: 'white',
        boxShadow: '0 4px 12px rgba(109, 40, 217, 0.3)'
      }
    },
    
    // Optimization Methods Header
    {
      id: 'methods-header',
      data: { 
        label: (
          <div style={{ padding: '15px', textAlign: 'center' }}>
            <strong style={{ fontSize: '18px', color: '#818CF8' }}>🧬 Optimization Methods</strong>
          </div>
        )
      },
      position: savedPositions?.['methods-header'] || { x: 450, y: 250 },
      style: {
        background: 'transparent',
        border: 'none',
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#818CF8'
      }
    },
    
    // Evolutionary Algorithm (was NSGA-II)
    {
      id: 'nsga2',
      data: { 
        label: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <strong style={{ 
              color: selectedMethod === 'nsga2' ? '#FFFFFF' : '#9CA3AF',
              fontSize: '18px'
            }}>
              🧬 Evolutionary Algorithm
            </strong>
            <div style={{ fontSize: '15px', marginTop: '12px', color: selectedMethod === 'nsga2' ? '#E0F2FE' : '#9CA3AF', lineHeight: '1.8' }}>
              <strong>Best for: Exploration</strong><br/>
              • Finds multiple solutions<br/>
              • No bias, pure optimization<br/>
              • Discovers trade-offs
            </div>
          </div>
        )
      },
      position: savedPositions?.['nsga2'] || { x: 70, y: 320 },
      style: {
        background: selectedMethod === 'nsga2' ? 
          'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)' : 
          'rgba(255, 255, 255, 0.08)',
        border: `2px solid ${selectedMethod === 'nsga2' ? '#0EA5E9' : 'transparent'}`,
        borderRadius: '10px',
        width: 200,
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: selectedMethod === 'nsga2' ? '0 4px 12px rgba(2, 132, 199, 0.3)' : 'none'
      },
      sourcePosition: Position.Top,
      targetPosition: Position.Bottom
    },
    
    // Weighted Sum
    {
      id: 'weighted-sum',
      data: { 
        label: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <strong style={{ 
              color: selectedMethod === 'weighted-sum' ? '#FFFFFF' : '#9CA3AF',
              fontSize: '18px'
            }}>
              ⚖️ Weighted Sum
            </strong>
            <div style={{ fontSize: '15px', marginTop: '12px', color: selectedMethod === 'weighted-sum' ? '#FEF3C7' : '#9CA3AF', lineHeight: '1.8' }}>
              <strong>Best for: Clear Goals</strong><br/>
              • Performance: 50%<br/>
              • Cost Savings: 30%<br/>
              • Maintainability: 20%
            </div>
          </div>
        )
      },
      position: savedPositions?.['weighted-sum'] || { x: 290, y: 320 },
      style: {
        background: selectedMethod === 'weighted-sum' ? 
          'linear-gradient(135deg, #D97706 0%, #B45309 100%)' : 
          'rgba(255, 255, 255, 0.08)',
        border: `2px solid ${selectedMethod === 'weighted-sum' ? '#F59E0B' : 'transparent'}`,
        borderRadius: '10px',
        width: 200,
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: selectedMethod === 'weighted-sum' ? '0 4px 12px rgba(217, 119, 6, 0.3)' : 'none'
      },
      sourcePosition: Position.Top,
      targetPosition: Position.Bottom
    },
    
    // Constraint Optimization
    {
      id: 'constraint-opt',
      data: { 
        label: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <strong style={{ 
              color: selectedMethod === 'constraint-opt' ? '#FFFFFF' : '#9CA3AF',
              fontSize: '18px'
            }}>
              🔒 Constraint Opt.
            </strong>
            <div style={{ fontSize: '15px', marginTop: '12px', color: selectedMethod === 'constraint-opt' ? '#EDE9FE' : '#9CA3AF', lineHeight: '1.8' }}>
              <strong>Best for: Compliance</strong><br/>
              • Response {'<'} 100ms<br/>
              • Memory {'<'} 256MB<br/>
              • 99.9% Uptime SLA
            </div>
          </div>
        )
      },
      position: savedPositions?.['constraint-opt'] || { x: 510, y: 320 },
      style: {
        background: selectedMethod === 'constraint-opt' ? 
          'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' : 
          'rgba(255, 255, 255, 0.08)',
        border: `2px solid ${selectedMethod === 'constraint-opt' ? '#8B5CF6' : 'transparent'}`,
        borderRadius: '10px',
        width: 200,
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: selectedMethod === 'constraint-opt' ? '0 4px 12px rgba(124, 58, 237, 0.3)' : 'none'
      },
      sourcePosition: Position.Top,
      targetPosition: Position.Bottom
    },
    
    // MOEA/D
    {
      id: 'moead',
      data: { 
        label: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <strong style={{ 
              color: selectedMethod === 'moead' ? '#FFFFFF' : '#9CA3AF',
              fontSize: '18px'
            }}>
              🎯 MOEA/D
            </strong>
            <div style={{ fontSize: '15px', marginTop: '12px', color: selectedMethod === 'moead' ? '#D1FAE5' : '#9CA3AF', lineHeight: '1.8' }}>
              <strong>Best for: Complex Apps</strong><br/>
              • Handles 10+ metrics<br/>
              • Scales efficiently<br/>
              • Adaptive optimization
            </div>
          </div>
        )
      },
      position: savedPositions?.['moead'] || { x: 730, y: 320 },
      style: {
        background: selectedMethod === 'moead' ? 
          'linear-gradient(135deg, #047857 0%, #065F46 100%)' : 
          'rgba(255, 255, 255, 0.08)',
        border: `2px solid ${selectedMethod === 'moead' ? '#10B981' : 'transparent'}`,
        borderRadius: '10px',
        width: 200,
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: selectedMethod === 'moead' ? '0 4px 12px rgba(4, 120, 87, 0.3)' : 'none'
      },
      sourcePosition: Position.Top,
      targetPosition: Position.Bottom
    },
    
    // Evolution Engine
    {
      id: 'artemis-evolution',
      data: { 
        label: (
          <div style={{ padding: '30px', textAlign: 'center' }}>
            <strong style={{ fontSize: '26px', color: '#FFFFFF' }}>⚡ Artemis Evolution Engine</strong>
            <div style={{ fontSize: '18px', marginTop: '15px', color: '#FFF7ED', lineHeight: '1.8' }}>
              Generates optimized code variations<br/>
              Learning from all feedback sources
            </div>
            <div style={{ 
              marginTop: '25px', 
              padding: '20px', 
              background: 'rgba(0, 0, 0, 0.3)', 
              borderRadius: '10px',
              fontSize: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <strong>Current Generation:</strong> #47<br/>
              <strong>Improvement:</strong> 3.2x faster, 75% less memory
            </div>
          </div>
        )
      },
      position: savedPositions?.['artemis-evolution'] || { x: 250, y: 650 },
      style: {
        background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)',
        border: '3px solid #F97316',
        borderRadius: '15px',
        width: 500,
        color: 'white',
        boxShadow: '0 10px 30px rgba(234, 88, 12, 0.3)'
      }
    },
    
    // Output
    {
      id: 'optimized-code',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'center' }}>
            <strong style={{ fontSize: '20px', color: '#FFFFFF' }}>✨ Continuously Optimized Code</strong>
            <div style={{ fontSize: '16px', marginTop: '15px', color: '#D1FAE5', lineHeight: '1.8' }}>
              Your code gets better with every iteration<br/>
              Adapting to new requirements automatically
            </div>
            <div style={{
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '8px',
              padding: '15px',
              marginTop: '20px',
              fontFamily: 'monospace',
              fontSize: '14px',
              color: '#E5E7EB',
              lineHeight: '1.6',
              textAlign: 'left'
            }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {`def process_data(data):
  # Optimized with parallel processing
  with ThreadPoolExecutor() as executor:
    results = list(executor.map(
      lambda x: x * 2, data
    ))
  return results`}
              </pre>
            </div>
          </div>
        )
      },
      position: savedPositions?.['optimized-code'] || { x: 300, y: 900 },
      style: {
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        border: '2px solid #10B981',
        borderRadius: '12px',
        width: 400,
        color: 'white',
        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
      }
    }
  ];

  const initialEdges = [
    // Objective to Methods
    {
      id: 'e1',
      source: 'objective-function',
      target: 'nsga2',
      type: 'smoothstep',
      style: { stroke: '#A78BFA', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#A78BFA' }
    },
    {
      id: 'e2',
      source: 'objective-function',
      target: 'weighted-sum',
      type: 'smoothstep',
      style: { stroke: '#A78BFA', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#A78BFA' }
    },
    {
      id: 'e3',
      source: 'objective-function',
      target: 'constraint-opt',
      type: 'smoothstep',
      style: { stroke: '#A78BFA', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#A78BFA' }
    },
    {
      id: 'e4',
      source: 'objective-function',
      target: 'moead',
      type: 'smoothstep',
      style: { stroke: '#A78BFA', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#A78BFA' }
    },
    
    // Methods to Evolution
    {
      id: 'e5',
      source: 'nsga2',
      target: 'artemis-evolution',
      type: 'smoothstep',
      style: { 
        stroke: selectedMethod === 'nsga2' ? '#22D3EE' : 'rgba(255, 255, 255, 0.1)', 
        strokeWidth: selectedMethod === 'nsga2' ? 3 : 1 
      },
      markerEnd: { type: MarkerType.ArrowClosed }
    },
    {
      id: 'e6',
      source: 'weighted-sum',
      target: 'artemis-evolution',
      type: 'smoothstep',
      style: { 
        stroke: selectedMethod === 'weighted-sum' ? '#F87171' : 'rgba(255, 255, 255, 0.1)', 
        strokeWidth: selectedMethod === 'weighted-sum' ? 3 : 1 
      },
      markerEnd: { type: MarkerType.ArrowClosed }
    },
    {
      id: 'e7',
      source: 'constraint-opt',
      target: 'artemis-evolution',
      type: 'smoothstep',
      style: { 
        stroke: selectedMethod === 'constraint-opt' ? '#C084FC' : 'rgba(255, 255, 255, 0.1)', 
        strokeWidth: selectedMethod === 'constraint-opt' ? 3 : 1 
      },
      markerEnd: { type: MarkerType.ArrowClosed }
    },
    {
      id: 'e8',
      source: 'moead',
      target: 'artemis-evolution',
      type: 'smoothstep',
      style: { 
        stroke: selectedMethod === 'moead' ? '#34D399' : 'rgba(255, 255, 255, 0.1)', 
        strokeWidth: selectedMethod === 'moead' ? 3 : 1 
      },
      markerEnd: { type: MarkerType.ArrowClosed }
    },
    
    // Evolution to Output
    {
      id: 'e9',
      source: 'artemis-evolution',
      target: 'optimized-code',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#FF8A50', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#FF8A50' }
    }
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Save positions whenever nodes change
  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    
    // Save positions to localStorage
    setNodes((nds) => {
      const positions = {};
      nds.forEach(node => {
        positions[node.id] = node.position;
      });
      localStorage.setItem('optimization-approaches-positions', JSON.stringify(positions));
      return nds;
    });
  }, [onNodesChange, setNodes]);

  const onNodeClick = (event, node) => {
    if (['nsga2', 'weighted-sum', 'constraint-opt', 'moead'].includes(node.id)) {
      setSelectedMethod(node.id);
    }
  };

  // Add reset button handler
  const handleResetPositions = () => {
    localStorage.removeItem('optimization-approaches-positions');
    window.location.reload();
  };

  return (
    <ReactFlowProvider>
      <div style={{
        height: '100vh',
        background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
        display: 'flex',
        flexDirection: 'column',
        color: 'white',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 30px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ 
                margin: 0, 
                fontSize: '28px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #EA580C, #FF6D00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🧬 Optimization Approaches
              </h1>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                Choose the best optimization method for your specific needs and watch the evolution engine work its magic
              </p>
            </div>
            
            {/* Method Selector Info */}
            <div style={{
              display: 'flex',
              gap: '15px',
              alignItems: 'center'
            }}>
              <div style={{
                padding: '10px 20px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                fontSize: '13px'
              }}>
                <span style={{ opacity: 0.7 }}>Selected Method:</span>{' '}
                <span style={{ fontWeight: '600', color: '#F97316' }}>
                  {selectedMethod === 'nsga2' && 'Evolutionary Algorithm'}
                  {selectedMethod === 'weighted-sum' && 'Weighted Sum'}
                  {selectedMethod === 'constraint-opt' && 'Constraint Opt.'}
                  {selectedMethod === 'moead' && 'MOEA/D'}
                </span>
              </div>
              
              {/* Reset Button */}
              <button
                onClick={handleResetPositions}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                🔄 Reset Layout
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            fitView
            fitViewOptions={{
              padding: 0.4,
              minZoom: 0.5,
              maxZoom: 1.2
            }}
            defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
            attributionPosition="bottom-left"
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
          >
            <Background 
              color="rgba(255, 255, 255, 0.05)" 
              gap={20} 
              size={1}
            />
            <Controls 
              style={{
                button: {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }
              }}
            />
            <MiniMap 
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              nodeColor={(node) => {
                if (node.style?.background?.includes('gradient')) {
                  return '#F4511E';
                }
                return 'rgba(255, 255, 255, 0.2)';
              }}
              maskColor="rgba(0, 0, 0, 0.7)"
            />
          </ReactFlow>
        </div>

        {/* Bottom Info Bar */}
        <div style={{
          padding: '15px 30px',
          background: 'rgba(0, 0, 0, 0.3)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'center',
          gap: '30px',
          fontSize: '13px'
        }}>
          <div style={{ opacity: 0.8 }}>
            <span style={{ color: '#8B5CF6', fontWeight: '600' }}>🎯 Smart Objectives</span> • 
            <span style={{ marginLeft: '8px' }}>Balances all your requirements</span>
          </div>
          <div style={{ opacity: 0.8 }}>
            <span style={{ color: '#F97316', fontWeight: '600' }}>⚡ Evolution Engine</span> • 
            <span style={{ marginLeft: '8px' }}>3.2x faster, 75% less memory</span>
          </div>
          <div style={{ opacity: 0.8 }}>
            <span style={{ color: '#10B981', fontWeight: '600' }}>✨ Optimized Code</span> • 
            <span style={{ marginLeft: '8px' }}>Continuously improving</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default OptimizationApproaches; 